import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

/**
 * RalphSessionController handles real-time updates and interactions
 * for the Ralph session dashboard.
 *
 * Connects to the RalphSessionChannel via ActionCable for live updates
 * and provides action methods for session control.
 */
export default class extends Controller {
  static values = {
    id: String
  }

  static targets = [
    "stateBadge",
    "controls",
    "stats",
    "loopCount",
    "taskProgress",
    "circuitState",
    "duration",
    "progressBar",
    "fixPlanContent",
    "fixPlanEdit",
    "loopTimeline"
  ]

  connect() {
    console.log("[RalphSession] Connecting to session:", this.idValue)
    this.subscribeToChannel()
  }

  disconnect() {
    console.log("[RalphSession] Disconnecting from session:", this.idValue)
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      {
        channel: "RalphSessionChannel",
        ralph_session_id: this.idValue
      },
      {
        connected: () => {
          console.log("[RalphSession] WebSocket connected")
        },
        disconnected: () => {
          console.log("[RalphSession] WebSocket disconnected")
        },
        received: (data) => {
          this.handleMessage(data)
        }
      }
    )
  }

  handleMessage(data) {
    console.log("[RalphSession] Received message:", data.type, data)

    switch (data.type) {
      case "state_change":
        this.handleStateChange(data)
        break
      case "loop_started":
        this.handleLoopStarted(data)
        break
      case "loop_completed":
        this.handleLoopCompleted(data)
        break
      case "loop_failed":
        this.handleLoopFailed(data)
        break
      case "session_completed":
        this.handleSessionCompleted(data)
        break
      case "circuit_breaker_opened":
        this.handleCircuitOpen(data)
        break
      case "status_update":
        this.handleStatusUpdate(data)
        break
      case "execution_started":
        this.handleExecutionStarted(data)
        break
      case "execution_completed":
        this.handleExecutionCompleted(data)
        break
      case "claude_message":
        this.handleClaudeMessage(data)
        break
      default:
        console.log("[RalphSession] Unknown message type:", data.type)
    }
  }

  handleStateChange(data) {
    // Update state badge
    if (this.hasStateBadgeTarget) {
      this.stateBadgeTarget.textContent = this.humanize(data.state)
      this.stateBadgeTarget.className = `badge badge-lg badge-${this.stateToBadgeClass(data.state)}`
    }

    // Update loop count
    if (this.hasLoopCountTarget && data.loop_count !== undefined) {
      this.loopCountTarget.textContent = data.loop_count
    }

    // Update circuit state
    if (this.hasCircuitStateTarget && data.circuit_state) {
      this.circuitStateTarget.textContent = this.humanize(data.circuit_state)
    }

    // Show notification
    this.showNotification(`Session ${data.event}`, "info")

    // Refresh controls based on new state
    this.refreshControls(data.state)
  }

  handleLoopStarted(data) {
    if (this.hasLoopCountTarget) {
      // Increment optimistically
      const current = parseInt(this.loopCountTarget.textContent) || 0
      this.loopCountTarget.textContent = Math.max(current, data.loop_number)
    }

    // Add running loop to timeline
    if (this.hasLoopTimelineTarget) {
      this.addLoopToTimeline({
        loop_number: data.loop_number,
        status: "running",
        started_at: data.timestamp
      })
    }

    this.showNotification(`Loop #${data.loop_number} started`, "info")
  }

  handleLoopCompleted(data) {
    // Update task progress
    if (this.hasTaskProgressTarget && data.fix_plan_progress) {
      const { completed, total, percentage } = data.fix_plan_progress
      this.taskProgressTarget.textContent = `${completed}/${total}`

      if (this.hasProgressBarTarget) {
        this.progressBarTarget.value = percentage
      }
    }

    // Update loop in timeline
    if (this.hasLoopTimelineTarget) {
      this.updateLoopInTimeline({
        loop_number: data.loop_number,
        status: "completed",
        had_progress: data.had_progress,
        work_type: data.work_type,
        work_summary: data.work_summary,
        files_changed: data.files_changed,
        tasks_completed: data.tasks_completed,
        exit_signal: data.exit_signal
      })
    }

    const progressText = data.had_progress ? "with progress" : "no progress"
    this.showNotification(`Loop #${data.loop_number} completed (${progressText})`, data.had_progress ? "success" : "warning")
  }

  handleLoopFailed(data) {
    if (this.hasLoopTimelineTarget) {
      this.updateLoopInTimeline({
        loop_number: data.loop_number,
        status: "failed",
        work_summary: data.error
      })
    }

    this.showNotification(`Loop #${data.loop_number} failed: ${data.error}`, "error")
  }

  handleSessionCompleted(data) {
    if (this.hasStateBadgeTarget) {
      this.stateBadgeTarget.textContent = "Completed"
      this.stateBadgeTarget.className = "badge badge-lg badge-success"
    }

    this.showNotification(`Session completed: ${data.reason}`, "success")
    this.refreshControls("completed")
  }

  handleCircuitOpen(data) {
    if (this.hasCircuitStateTarget) {
      this.circuitStateTarget.textContent = "Open"
    }

    this.showNotification("Circuit breaker opened - no progress detected", "error")
    this.refreshControls("circuit_open")
  }

  handleStatusUpdate(data) {
    if (this.hasStateBadgeTarget) {
      this.stateBadgeTarget.textContent = this.humanize(data.status)
    }

    if (this.hasLoopCountTarget && data.loop_count !== undefined) {
      this.loopCountTarget.textContent = data.loop_count
    }
  }

  handleExecutionStarted(data) {
    console.log("[RalphSession] Execution started for loop", data.loop_number)
  }

  handleExecutionCompleted(data) {
    console.log("[RalphSession] Execution completed in", data.execution_time, "seconds")
  }

  handleClaudeMessage(data) {
    // Could show real-time Claude output if desired
    console.log("[RalphSession] Claude message:", data.message_type)
  }

  // Action methods

  start(event) {
    // Don't prevent default - let the form submit
    this.showNotification("Starting session...", "info")
  }

  pause(event) {
    // Don't prevent default - let the form submit
    this.showNotification("Pausing session...", "info")
  }

  resume(event) {
    // Don't prevent default - let the form submit
    this.showNotification("Resuming session...", "info")
  }

  resetCircuit(event) {
    // Don't prevent default - let the form submit
    this.showNotification("Resetting circuit breaker...", "info")
  }

  toggleFixPlanEdit() {
    if (this.hasFixPlanContentTarget && this.hasFixPlanEditTarget) {
      this.fixPlanContentTarget.classList.toggle("hidden")
      this.fixPlanEditTarget.classList.toggle("hidden")
    }
  }

  // Helper methods

  addLoopToTimeline(loop) {
    // This would add a new loop entry to the timeline
    // For now, we'll reload the page to show new loops
    // In a full implementation, this would insert HTML dynamically
    console.log("[RalphSession] Adding loop to timeline:", loop)
  }

  updateLoopInTimeline(loop) {
    // This would update an existing loop entry
    console.log("[RalphSession] Updating loop in timeline:", loop)
    // For now, we reload to show updates
    // A full implementation would update the specific element
  }

  refreshControls(state) {
    // Reload the page to get updated controls
    // In a full implementation, this would use Turbo Streams
    setTimeout(() => {
      window.location.reload()
    }, 1000)
  }

  stateToBadgeClass(state) {
    const classes = {
      idle: "ghost",
      running: "warning",
      paused: "info",
      completed: "success",
      circuit_open: "error",
      failed: "error"
    }
    return classes[state] || "ghost"
  }

  humanize(str) {
    if (!str) return ""
    return str.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }

  showNotification(message, type = "info") {
    // Create toast notification
    const toast = document.createElement("div")
    toast.className = `alert alert-${type} shadow-lg fixed bottom-4 right-4 w-auto max-w-md z-50`
    toast.innerHTML = `
      <div>
        <span>${message}</span>
      </div>
      <button class="btn btn-sm btn-ghost" onclick="this.parentElement.remove()">×</button>
    `

    document.body.appendChild(toast)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.remove()
    }, 5000)
  }
}
